import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createStateHook,
	createStore,
} from 'react-sweet-state';

import { type UserSearchItem } from '@atlaskit/smart-common';
import { type QuickFindAllActivityDataEdge, type SearchPageNode } from '@atlassian/search-client';

export type QuickFindState = {
	quickFindOpen: boolean;
	isFeedbackModalOpen: boolean;

	/**
	 * Query used to determine what should be shown in the dialog.
	 */
	resultsQuery: string;

	preQueryLoading: boolean;
	postQueryLoading: boolean;

	preQueryError?: Error;
	postQueryError?: Error;

	retryCount: number;

	recentQueries: string[];

	confluenceSpaces: {
		result: SearchPageNode[];
		duration: number;
	};
	recentActivities: {
		result: QuickFindAllActivityDataEdge[];
		duration: number;
	};
	peopleAndTeams: {
		result: UserSearchItem[];
		duration: number;
	};
	searchResults: {
		result: SearchPageNode[];
		duration: number;
	};
	suggestedQueries: {
		result: string[];
		duration: number;
	};

	updateRecentQueries?: () => void;
};

export const initialState = {
	quickFindOpen: false,
	isFeedbackModalOpen: false,
	resultsQuery: '',

	preQueryLoading: false,
	postQueryLoading: false,

	preQueryError: undefined,
	postQueryError: undefined,

	retryCount: 0,

	recentQueries: [],

	confluenceSpaces: { result: [], duration: -1 },
	recentActivities: { result: [], duration: -1 },
	peopleAndTeams: { result: [], duration: -1 },
	searchResults: { result: [], duration: -1 },
	suggestedQueries: { result: [], duration: -1 },

	updateRecentQueries: undefined,
} satisfies QuickFindState;

const actions = {
	setQuickFindOpen:
		(quickFindOpen: QuickFindState['quickFindOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ quickFindOpen });
		},
	setIsFeedbackModalOpen:
		(feedbackOpen: QuickFindState['isFeedbackModalOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ isFeedbackModalOpen: feedbackOpen });
		},
	setResultsQuery:
		(resultsQuery: QuickFindState['resultsQuery']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ resultsQuery });
		},
	setPreQueryLoading:
		(preQueryLoading: QuickFindState['preQueryLoading']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ preQueryLoading });
		},
	setPostQueryLoading:
		(postQueryLoading: QuickFindState['postQueryLoading']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ postQueryLoading });
		},
	setPreQueryError:
		(preQueryError: QuickFindState['preQueryError']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ preQueryError });
		},
	setPostQueryError:
		(postQueryError: QuickFindState['postQueryError']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ postQueryError });
		},
	setRetryCount:
		(retryCount: QuickFindState['retryCount']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ retryCount });
		},
	setConfluenceSpaces:
		(confluenceSpaces: QuickFindState['confluenceSpaces']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ confluenceSpaces });
		},
	setRecentQueries:
		(recentQueries: QuickFindState['recentQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ recentQueries });
		},
	setRecentActivities:
		(recentActivities: QuickFindState['recentActivities']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ recentActivities });
		},
	setPeopleAndTeams:
		(peopleAndTeams: QuickFindState['peopleAndTeams']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ peopleAndTeams });
		},
	setSearchResults:
		(searchResults: QuickFindState['searchResults']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ searchResults });
		},
	setSuggestedQueries:
		(suggestedQueries: QuickFindState['suggestedQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ suggestedQueries });
		},
	setUpdateRecentQueries:
		(updateRecentQueries: QuickFindState['updateRecentQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ updateRecentQueries });
		},
};

type TActions = typeof actions;

const Store = createStore<QuickFindState, TActions>({
	initialState,
	actions,
	name: 'quick-find',
});

export const useQuickFindActions = createActionsHook(Store);

export const useQuickFindOpen = createStateHook(Store, {
	selector: (state) => state.quickFindOpen,
});

export const useIsFeedbackModalOpen = createHook(Store, {
	selector: (state) => state.isFeedbackModalOpen,
});

export const useResultsQuery = createStateHook(Store, { selector: (state) => state.resultsQuery });

export const usePreQueryLoading = createStateHook(Store, {
	selector: (state) => state.preQueryLoading,
});

export const usePostQueryLoading = createStateHook(Store, {
	selector: (state) => state.postQueryLoading,
});

export const usePreQueryError = createStateHook(Store, {
	selector: (state) => state.preQueryError,
});

export const usePostQueryError = createStateHook(Store, {
	selector: (state) => state.postQueryError,
});

export const useRetryCount = createStateHook(Store, { selector: (state) => state.retryCount });

export const useConfluenceSpaces = createStateHook(Store, {
	selector: (state) => state.confluenceSpaces,
});

export const useRecentQueries = createStateHook(Store, {
	selector: (state) => state.recentQueries,
});

export const useRecentActivities = createStateHook(Store, {
	selector: (state) => state.recentActivities,
});

export const usePeopleAndTeams = createStateHook(Store, {
	selector: (state) => state.peopleAndTeams,
});

export const useSearchResults = createStateHook(Store, {
	selector: (state) => state.searchResults,
});

export const useSuggestedQueries = createStateHook(Store, {
	selector: (state) => state.suggestedQueries,
});

export const useUpdateRecentQueries = createStateHook(Store, {
	selector: (state) => state.updateRecentQueries,
});

/**
 * This store is contained within a container as we may need to support multiple
 * instances of the Quick Find component in Nav3. This would be the case if we had
 * one in the navigation bar and another one in the search page.
 */
export const QuickFindStoreContainer = createContainer(Store);
